import { useCallback, useEffect, useMemo } from 'react';
import isEqual from 'react-fast-compare';
import { useForm } from 'react-hook-form';
import { useActivePosEntityContext } from 'src/contexts/ActivePosEntityContext';
import { useAppContext } from 'src/contexts/AppContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { useGetListingAutoPricingSettings } from 'src/hooks/useGetListingAutoPricingSettings';
import { isSuccess } from 'src/utils/errorUtils';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  Listing,
  ListingClient,
  ListingDetails,
  ListingDetailsAutoPricingSectionUpdates,
} from 'src/WebApiController';

export const useAutoPricingForm = (
  listing: Listing | null | undefined,
  onSubmitStart: () => void,
  onSubmitDone: (success?: boolean) => Promise<void>
) => {
  const { activeAccountWebClientConfig } = useAppContext();
  const { showErrorDialog } = useClientLogContext();
  const { event } = useActivePosEntityContext<ListingDetails>();
  const {
    pricingSettings: listingPricingSettings,
    loaded: listingSettingLoaded,
  } = useGetListingAutoPricingSettings(listing, event);

  const methods = useForm<ListingDetailsAutoPricingSectionUpdates>({
    defaultValues: {},
  });

  useEffect(() => {
    if (listingSettingLoaded) {
      methods.reset(listingPricingSettings ?? {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listingPricingSettings, listingSettingLoaded]);

  const onSubmitHandler = useCallback(
    async (autoPricingSectionForm: ListingDetailsAutoPricingSectionUpdates) => {
      tryInvokeApi(
        async () => {
          onSubmitStart();
          const result = await new ListingClient(
            activeAccountWebClientConfig
          ).updateListingAutoPricingSectionByListingId(
            autoPricingSectionForm.id,
            autoPricingSectionForm
          );

          if (isSuccess(result)) {
            // Reset the form to latest changed
            methods.reset(autoPricingSectionForm);
            await onSubmitDone();
          } else {
            showErrorDialog(
              'ListingClient.updateListingAutoPricingSection',
              { message: result.message, status: result.status },
              {
                trackErrorData: {
                  autoPricingSectionForm,
                },
              }
            );
          }
        },
        (error) => {
          showErrorDialog(
            'ListingClient.updateListingAutoPricingSection',
            error,
            {
              trackErrorData: autoPricingSectionForm,
            }
          );
        },
        () => {
          onSubmitDone(false);
        }
      );
    },
    [
      activeAccountWebClientConfig,
      methods,
      onSubmitDone,
      onSubmitStart,
      showErrorDialog,
    ]
  );

  const onSubmit = useCallback(() => {
    methods.handleSubmit(onSubmitHandler)();
  }, [methods, onSubmitHandler]);

  const onReset = useCallback(() => {
    methods.reset(listingPricingSettings ?? {});
  }, [listingPricingSettings, methods]);

  const input = methods.watch();

  const hasChanges = useMemo(() => {
    const defaultValues = methods.formState.defaultValues;

    return (
      input.netProceedsFloor !== defaultValues?.netProceedsFloor ||
      input.netProceedsCeiling !== defaultValues?.netProceedsCeiling ||
      input.currencyCode !== defaultValues?.currencyCode ||
      input.autoPricingEnabled !== defaultValues?.autoPricingEnabled ||
      input.compListingFloor !== defaultValues?.compListingFloor ||
      input.compListingCeiling !== defaultValues?.compListingCeiling ||
      input.compListingMode !== defaultValues?.compListingMode ||
      input.compListingOnlyForSameZoneEnabled !==
        defaultValues?.compListingOnlyForSameZoneEnabled ||
      input.compListingOnlyForSelectedSectionsEnabled !==
        defaultValues?.compListingOnlyForSelectedSectionsEnabled ||
      input.compListingExcludeFanInventory !==
        defaultValues?.compListingExcludeFanInventory ||
      input.compListingExcludeDefects !==
        defaultValues?.compListingExcludeDefects ||
      input.compListingIncludeUnmappedInventory !==
        defaultValues?.compListingIncludeUnmappedInventory ||
      input.compListingQuantityScoreAdjustmentEnabled !==
        defaultValues?.compListingQuantityScoreAdjustmentEnabled ||
      input.compListingQuantityScoreAdjustmentOverrideJson !==
        defaultValues?.compListingQuantityScoreAdjustmentOverrideJson ||
      input.undercutMode !== defaultValues?.undercutMode ||
      input.undercutAbsoluteAmount !== defaultValues?.undercutAbsoluteAmount ||
      input.undercutRelativeAmount !== defaultValues?.undercutRelativeAmount ||
      input.circuitBreakerMaxDiscountVelocityPercent !==
        defaultValues?.circuitBreakerMaxDiscountVelocityPercent ||
      input.circuitBreakerMaxDiscountVelocityTicksInHours !==
        defaultValues?.circuitBreakerMaxDiscountVelocityTicksInHours ||
      input.circuitBreakerMinCompListingCount !==
        defaultValues?.circuitBreakerMinCompListingCount ||
      input.circuitBreakerRelativeCeiling !==
        defaultValues?.circuitBreakerRelativeCeiling ||
      input.circuitBreakerRelativeFloor !==
        defaultValues?.circuitBreakerRelativeFloor ||
      input.outlierMode !== defaultValues?.outlierMode ||
      input.outlierStandardDeviations !==
        defaultValues?.outlierStandardDeviations ||
      input.outlierKthLowestLimit !== defaultValues?.outlierKthLowestLimit ||
      input.outlierKthLowestLimitRelativeSpacing !==
        defaultValues?.outlierKthLowestLimitRelativeSpacing ||
      input.outlierKthLowestLimitAbsoluteSpacing !==
        defaultValues?.outlierKthLowestLimitAbsoluteSpacing ||
      input.skipAdvancePricing !== defaultValues?.skipAdvancePricing ||
      input.skipAutoPricing !== defaultValues?.skipAutoPricing ||
      input.skipCompListing !== defaultValues?.skipCompListing ||
      input.skipUndercut !== defaultValues?.skipUndercut ||
      !isEqual(
        input.compListingSelectedSectionSettings,
        defaultValues?.compListingSelectedSectionSettings
      ) ||
      !isEqual(
        input.compListingQuantityFilters,
        defaultValues?.compListingQuantityFilters
      ) ||
      input.undercutReferenceListingPosition !==
        defaultValues?.undercutReferenceListingPosition
    );
  }, [input, methods.formState.defaultValues]);

  const hasSelectedSections = useMemo(() => {
    return (
      (input.compListingSelectedSectionSettings?.sectionIdFilter?.length ?? 0) >
      0
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(input.compListingSelectedSectionSettings)]);

  return {
    hasChanges,
    hasSelectedSections,
    methods,
    onSubmit,
    onReset,
  };
};
